/**
 * CommandPalette
 * Global Cmd+K command palette — search estimates, invoices, customers, work orders.
 */
import { useEffect, useRef, useState, useCallback } from 'react';
import WMIcon from './WMIcon';
import { useCommandPalette } from '../../hooks/useCommandPalette';

// ── Type label map ────────────────────────────────────────────────────────────
const TYPE_LABELS = {
  shortcut:  'Shortcuts',
  estimate:  'Estimates',
  invoice:   'Invoices',
  customer:  'Customers',
  workorder: 'Work Orders',
};

const TYPE_BADGE_COLORS = {
  shortcut:  'bg-gray-100 text-gray-500 dark:bg-[#243348] dark:text-[#7D93AE]',
  estimate:  'bg-blue-50 text-blue-600 dark:bg-blue-900/30 dark:text-blue-300',
  invoice:   'bg-emerald-50 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-300',
  customer:  'bg-purple-50 text-purple-600 dark:bg-purple-900/30 dark:text-purple-300',
  workorder: 'bg-amber-50 text-amber-600 dark:bg-amber-900/30 dark:text-amber-300',
};

export default function CommandPalette({ open, onClose, onNavigate }) {
  const { query, setQuery, results } = useCommandPalette();
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  // Focus input when opened
  /* eslint-disable react-hooks/set-state-in-effect */
  useEffect(() => {
    if (open) {
      setActiveIndex(0);
      setTimeout(() => inputRef.current?.focus(), 50);
    } else {
      setQuery('');
    }
  }, [open, setQuery]);

  // Reset active index when results change
  useEffect(() => {
    setActiveIndex(0);
  }, [results]);
  /* eslint-enable react-hooks/set-state-in-effect */

  // Scroll active item into view
  useEffect(() => {
    if (!listRef.current) return;
    const active = listRef.current.querySelector('[data-active="true"]');
    active?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  const handleSelect = useCallback((item) => {
    item.onSelect(onNavigate);
    onClose();
  }, [onNavigate, onClose]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape') {
      if (query) {
        setQuery('');
      } else {
        onClose();
      }
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, results.length - 1));
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      if (results[activeIndex]) {
        handleSelect(results[activeIndex]);
      }
    }
  }, [query, setQuery, onClose, results, activeIndex, handleSelect]);

  if (!open) return null;

  // Group results by type
  const grouped = results.reduce((acc, item, idx) => {
    const type = item.type;
    if (!acc[type]) acc[type] = [];
    acc[type].push({ ...item, _idx: idx });
    return acc;
  }, {});

  const groupOrder = ['shortcut', 'estimate', 'invoice', 'customer', 'workorder'];

  return (
    // Overlay
    <div
      className="fixed inset-0 z-50 flex items-start justify-center pt-[10vh] px-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Command palette"
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />

      {/* Panel */}
      <div
        className="relative w-full max-w-lg bg-white dark:bg-[#1B2A3E] rounded-xl shadow-2xl border border-gray-200 dark:border-[#243348] overflow-hidden flex flex-col animate-palette-in"
        onClick={(e) => e.stopPropagation()}
        style={{
          animation: 'paletteIn 0.15s ease-out both',
          maxHeight: '70vh',
        }}
      >
        {/* Search bar */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-200 dark:border-[#243348] flex-shrink-0">
          <WMIcon name="magnifying-glass" className="w-5 h-5 text-gray-400 dark:text-[#7D93AE] flex-shrink-0" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Search estimates, invoices, customers..."
            className="flex-1 bg-transparent text-[var(--wm-text-body)] text-[#0F1923] dark:text-[#F8FAFE] placeholder-gray-400 dark:placeholder-[#7D93AE] outline-none"
          />
          {query && (
            <button
              onClick={() => setQuery('')}
              className="text-gray-400 hover:text-gray-600 dark:text-[#7D93AE] dark:hover:text-[#F8FAFE] transition-colors"
              aria-label="Clear search"
            >
              <WMIcon name="x-mark" className="w-4 h-4" />
            </button>
          )}
          <kbd className="hidden sm:flex items-center gap-0.5 px-1.5 py-0.5 text-[var(--wm-text-caption)] font-medium text-gray-400 dark:text-[#7D93AE] bg-gray-100 dark:bg-[#243348] rounded border border-gray-200 dark:border-[#2E4060] flex-shrink-0">
            Esc
          </kbd>
        </div>

        {/* Results */}
        <div
          ref={listRef}
          className="overflow-y-auto flex-1 py-2"
          role="listbox"
        >
          {results.length === 0 ? (
            <div className="px-4 py-8 text-center text-[var(--wm-text-body)] text-gray-400 dark:text-[#7D93AE]">
              {query
                ? <>No results for <span className="font-medium text-[#0F1923] dark:text-[#F8FAFE]">"{query}"</span></>
                : 'Start typing to search...'}
            </div>
          ) : (
            groupOrder.map((type) => {
              const items = grouped[type];
              if (!items?.length) return null;
              return (
                <div key={type}>
                  {/* Section header */}
                  <div className="px-4 py-1.5">
                    <span className="text-[var(--wm-text-caption)] font-semibold uppercase tracking-widest text-gray-400 dark:text-[#7D93AE]">
                      {TYPE_LABELS[type] || type}
                    </span>
                  </div>
                  {/* Items */}
                  {items.map((item) => {
                    const isActive = item._idx === activeIndex;
                    return (
                      <button
                        key={item.id}
                        data-active={isActive}
                        role="option"
                        aria-selected={isActive}
                        onClick={() => handleSelect(item)}
                        onMouseEnter={() => setActiveIndex(item._idx)}
                        className={[
                          'w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors',
                          isActive
                            ? 'bg-[#2E8BF0]/10 dark:bg-[#2E8BF0]/15'
                            : 'hover:bg-gray-50 dark:hover:bg-[#243348]/60',
                        ].join(' ')}
                      >
                        {/* Icon */}
                        <span className={[
                          'flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center',
                          isActive
                            ? 'bg-[#2E8BF0] text-white'
                            : 'bg-gray-100 dark:bg-[#243348] text-gray-500 dark:text-[#7D93AE]',
                        ].join(' ')}>
                          <WMIcon name={item.icon} className="w-4 h-4" />
                        </span>

                        {/* Text */}
                        <div className="flex-1 min-w-0">
                          <div className="text-[var(--wm-text-body)] font-medium text-[#0F1923] dark:text-[#F8FAFE] truncate">
                            {item.title}
                          </div>
                          {item.subtitle && (
                            <div className="text-[var(--wm-text-meta)] text-gray-400 dark:text-[#7D93AE] truncate mt-0.5">
                              {item.subtitle}
                            </div>
                          )}
                        </div>

                        {/* Type badge */}
                        <span className={[
                          'flex-shrink-0 text-[var(--wm-text-caption)] font-medium px-1.5 py-0.5 rounded hidden sm:block',
                          TYPE_BADGE_COLORS[item.type] || TYPE_BADGE_COLORS.shortcut,
                        ].join(' ')}>
                          {TYPE_LABELS[item.type] || item.type}
                        </span>

                        {/* Enter hint for active */}
                        {isActive && (
                          <kbd className="flex-shrink-0 flex items-center px-1.5 py-0.5 text-[var(--wm-text-caption)] font-medium text-gray-400 dark:text-[#7D93AE] bg-gray-100 dark:bg-[#243348] rounded border border-gray-200 dark:border-[#2E4060]">
                            ↵
                          </kbd>
                        )}
                      </button>
                    );
                  })}
                </div>
              );
            })
          )}
        </div>

        {/* Footer */}
        <div className="flex-shrink-0 border-t border-gray-200 dark:border-[#243348] px-4 py-2 flex items-center gap-4 text-[var(--wm-text-caption)] text-gray-400 dark:text-[#7D93AE]">
          <span className="flex items-center gap-1">
            <kbd className="px-1 py-0.5 bg-gray-100 dark:bg-[#243348] rounded border border-gray-200 dark:border-[#2E4060]">↑↓</kbd>
            Navigate
          </span>
          <span className="flex items-center gap-1">
            <kbd className="px-1 py-0.5 bg-gray-100 dark:bg-[#243348] rounded border border-gray-200 dark:border-[#2E4060]">↵</kbd>
            Select
          </span>
          <span className="flex items-center gap-1">
            <kbd className="px-1 py-0.5 bg-gray-100 dark:bg-[#243348] rounded border border-gray-200 dark:border-[#2E4060]">Esc</kbd>
            Close
          </span>
        </div>
      </div>

      {/* Animation keyframes — injected inline to avoid Tailwind config changes */}
      <style>{`
        @keyframes paletteIn {
          from { opacity: 0; transform: scale(0.96) translateY(-8px); }
          to   { opacity: 1; transform: scale(1)    translateY(0); }
        }
      `}</style>
    </div>
  );
}
